<template>
  <div>

    <div v-if="sale.state === 'canceled' || sale.state === 'closed'"  class="investing">
      <div v-if="isInvested">
        <span class="text_medium">{{ i18n.sale_invested_header() }}</span>
        <span class="text_medium text_bold">{{ roundCurrency(form.amount, form.quoteAsset) }} {{ form.quoteAsset }}</span>
      </div>
      <div class="text_description text_medium">
        <p>{{ i18n.sale_invested_closed_text() }}</p>
      </div>
    </div>

    <div v-else class="investing">
      <div v-if="isInvested">
        <span class="text_medium text_description">{{ i18n.sale_invested_amount_text() }}</span>
        <span class="text_medium text_bold">{{ form.amount }} {{ form.quoteAsset }}</span>
        <div class="investing-block">
          <md-button class="btn md-primary invest__edit-btn"
                     @click="changeInvesting"
                     v-if="sale.isOpened && offer"
                     :disabled="isPending"
          >
            <div><span>{{ i18n.sale_invested_change_amount_bt() }}</span></div>
            <div><md-icon>edit</md-icon></div>
          </md-button>
          <md-button class="btn md-primary invest__cancel-btn"
                     @click="cancelOffer"
                     v-if="sale.isOpened && offer"
                     :disabled="isPending">
            <div><span>{{ i18n.sale_invested_cancel_bt() }}</span></div>
            <div><md-icon>clear</md-icon></div>
          </md-button>

        </div>
      </div>
      <div v-else>
        <div class="investing-block__balance text_description"
             :class="{ 'invest__available--error': limitExceeded }"
        >
          {{ i18n.sale_invested_your_balance() }}:
          <span class="invest__available-amount">{{ i18n.c(roundCurrency(available, this.form.quoteAsset)) }} </span>
        </div>
        <div class="investing-block">

          <input-field class="invest__input-field field__number"
                       title="Your investment"
                       v-model="form.amount"
                       id="investment"
                       name="investment"
                       type="number"
                       :label="i18n.lbl_investment()"
                       placeholder="0"
                       :errorMessage="errorMessage('investment')"
                       v-validate="'amount'"
                       data-vv-validate-on="input"
          />

          <select-field v-model="form.quoteAsset"
                        :values="sale.quoteAssetCodes"
                        class="invest__select-field"/>

          <md-button class="btn md-primary btn_narrow"
                     @click="invest"
                     :disabled="isPending || hardCapExceeded || !sale.isOpened || sale.isUpcoming || !form.amount || limitExceeded">
            {{i18n.sale_invest()}}
          </md-button>
          <md-tooltip v-if="sale.isUpcoming"
                      :class="'info-tooltip'" md-direction="top">{{ i18n.sale_invested_is_upcoming() }}</md-tooltip>
          <md-tooltip v-else-if="hardCapExceeded"
                      :class="'info-tooltip'" md-direction="top">{{ i18n.sale_invested_hard_cap_exceeded() }}: {{ i18n.cc(sale.hardCap) }}</md-tooltip>
          <md-tooltip v-else-if="sale.isClosed"
                      :class="'info-tooltip'" md-direction="top">{{ i18n.sale_invested_is_closed() }}</md-tooltip>
          <md-tooltip v-else-if="sale.isCanceled"
                      :class="'info-tooltip'" md-direction="top">{{ i18n.sale_invested_is_canceled() }}</md-tooltip>
          <md-tooltip v-else-if="limitExceeded"
                      :class="'info-tooltip'" md-direction="top">{{ i18n.sale_invested_limit_exceeded() }}</md-tooltip>
        </div>

        <div class="row">
          <template>
            <div class="fund-info" v-if="sale.type === 'equity'">
              <md-icon md-src="/static/img/info_icon.svg" class="fund-info__icon"/>
              <div class="fund-info__description">
                <p>{{ i18n.sale_invested_your_profit() }}</p>
                <p>{{ equityProfit }}</p>
              </div>
            </div>
            <div class="fund-info" v-if="sale.type === 'deposit'">
              <md-icon md-src="/static/img/info_icon.svg" class="fund-info__icon"/>
              <div class="fund-info__description">
                <p>{{ i18n.sale_invested_your_profit() }} {{ depositProfit }} {{ form.quoteAsset }}</p>
              </div>
            </div>
          </template>
        </div>

      </div>
  </div>
  </div>
</template>

<script>
  import FormMixin from '@com/mixins/form.mixin'
  import { i18n } from '@js/i18n'
  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import _get from 'lodash/get'

  import { offersService } from '@api/offer.service'
  import { accountsService } from '@api/accounts.service'
  import { pairsService } from '@api/pairs.service'
  import { feeService } from '@api/fees.service'

  import { ErrorHandler } from '@js/errors/error_handler'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { add, subtract, multiply } from '@utils/math.util'
  import { RecordFactory } from '@js/records/factory'
  import { commonEvents } from '@js/events/common_events'
  import CurrencyRound from '@com/mixins/currency.round.mixin'
  import {errors} from '@js/errors/factory'

export default {
    name: 'sale-invest',
    mixins: [FormMixin, CurrencyRound],
    props: ['sale'],
    components: {},
    data: _ => ({
      investShow: false,
      form: {
        quoteAsset: '',
        amount: '',
        convertedAmount: 0
      },
      investedAmount: 0,
      offers: [],
      i18n
    }),
    created () {
      this.setTokenCode()
      this.loadOffers()
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances,
        vuexTypes.accountId,
        vuexTypes.factorData
      ]),
      equityProfit () {
        const amountFrom = parseFloat(this.form.amount) / 100 * this.sale.profitability_from
        const amountTo = parseFloat(this.form.amount) / 100 * this.sale.profitability_to
        return (!isNaN(amountFrom) && !isNaN(amountTo)) ? i18n.sale_profit_from_to({
          from: this.roundCurrency(amountFrom.toPrecision(6), this.form.quoteAsset) + this.form.quoteAsset,
          to: this.roundCurrency(amountTo.toPrecision(6), this.form.quoteAsset) + this.form.quoteAsset
        }) : i18n.sale_profit_from_to({from: 0 + this.form.quoteAsset, to: 0 + this.form.quoteAsset})
      },
      depositProfit () {
        const amount = parseFloat(this.form.amount) / 100 * this.sale.annualPercentage
        return !isNaN(amount) ? this.roundCurrency(amount.toPrecision(6), this.form.quoteAsset) : 0
      },
      isInvested () {
        return this.form.amount > 0 && this.investShow
      },
      offer () {
        return this.offers.find(
          offer => offer.quoteAssetCode === this.form.quoteAsset &&
                   offer.baseAssetCode === this.sale.baseAsset) || null
      },
      price () {
        return this.sale.quoteAssetPrices[this.form.quoteAsset] || '1'
      },
      available () {
        const balance = add(
          _get(this.accountBalances, `${this.form.quoteAsset}.balance`, 0),
          _get(this.offer, 'baseAmount', 0))
        return balance
      },
      limitExceeded () {
        if (!this.form.amount) return false
        const left = this.available ? subtract(this.available, this.form.amount) : 0
        return left < 0
      },
      hardCapExceeded () {
        return parseFloat(this.form.convertedAmount) > parseFloat(add(this.sale.hardCap, 1))
      },
      maxValue () {
        const hardCap = this.sale.hardCaps[this.form.quoteAsset]
        const currentCap = this.sale.totalCurrentCaps[this.form.quoteAsset]
        const lastOfferAmount = this.offer ? this.offer.quoteAmount : 0
        const balance = this.accountBalances[this.form.quoteAsset].balance
        const totalBalance = add(balance, (lastOfferAmount || 0))
        const sub = add(subtract(hardCap, currentCap), lastOfferAmount)
        if (parseFloat(totalBalance) > parseFloat(sub)) {
          return sub
        } else {
          return totalBalance
        }
      }
    },
    methods: {
      ...mapActions({
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES,
        updateTwoFactorData: vuexTypes.SET_TWO_FACTOR_STATE
      }),
      changeInvesting () {
        this.investShow = !this.investShow
      },
      setTokenCode () {
        this.form.quoteAsset = this.sale.quoteAssetCodes[0] || null
      },
      async loadOffers () {
        const response = await offersService.loadUserSaleOffers(this.sale.id)
        const records = response.records
        this.offers = records.map(record => RecordFactory.createOfferRecord(record))
      },
      async invest () {
        if (!await this.isValid()) return
        this.disable()

        try {
          if (!this.accountBalances[this.form.quoteAsset]) {
            await accountsService.createBalance(this.form.quoteAsset)
            await this.loadBalances()
          }

          if (!this.accountBalances[this.sale.baseAsset]) {
            await accountsService.createBalance(this.sale.baseAsset)
            await this.loadBalances()
          }
          const offerFees = await feeService.loadOfferFeeByAmount(this.form.quoteAsset, multiply(this.form.amount, this.price))

          const cancelOpts = this.offer ? {
            baseBalance: _get(this.accountBalances, `${this.sale.baseAsset}.balance_id`),
            quoteBalance: _get(this.accountBalances, `${this.form.quoteAsset}.balance_id`),
            offerId: this.offer.id,
            price: this.price,
            orderBookId: this.sale.id
          } : null
          const createOpts = this.form.amount > 0 ? {
            amount: this.form.amount,
            price: this.price,
            orderBookId: this.sale.id,
            isBuy: true,
            baseBalance: _get(this.accountBalances, `${this.sale.baseAsset}.balance_id`),
            quoteBalance: _get(this.accountBalances, `${this.form.quoteAsset}.balance_id`),
            fee: offerFees.percent
          } : null
          await offersService.createSaleOffer(createOpts, cancelOpts)
          this.$emit(commonEvents.investInSale)
          this.loadBalances()
          this.loadOffers()
          EventDispatcher.dispatchShowSuccessEvent(i18n.sale_offer_created({asset: this.sale.baseAsset}))
        } catch (error) {
          if (error instanceof errors.TFAExceededCodeError) {
            EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
          } else {
            ErrorHandler.processUnexpected(error)
          }
        }

        this.$emit('onChangeCamapignData')
        this.enable()
      },
      async cancelOffer () {
        this.disable()
        try {
          const cancelOpts = this.offer ? {
            baseBalance: _get(this.accountBalances, `${this.sale.baseAsset}.balance_id`),
            quoteBalance: _get(this.accountBalances, `${this.form.quoteAsset}.balance_id`),
            offerId: this.offer.id,
            price: this.price,
            orderBookId: this.sale.id
          } : null
          await offersService.cancelOffer(cancelOpts)
          this.$emit(commonEvents.investInSale)
          this.loadBalances()
          this.loadOffers()
          EventDispatcher.dispatchShowSuccessEvent(i18n.sale_offer_cancelled({asset: this.sale.baseAsset}))
        } catch (error) {
          if (error instanceof errors.TFAExceededCodeError) {
            EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
          } else {
            ErrorHandler.processUnexpected(error)
          }
        }
        this.enable()
      }
    },
    watch: {
      'form.amount': async function (value) {
        if (value !== '' && value > 0) {
          if (this.form.quoteAsset === this.sale.defaultQuoteAsset) {
            this.form.convertedAmount = this.form.amount
          } else {
            this.form.convertedAmount = await pairsService.loadConvertedAmount(this.form.amount, this.form.quoteAsset, this.sale.defaultQuoteAsset)
          }
        }
        if (value === '') {
          this.form.convertedAmount = 0
        }
      },
      offer: function (value) {
        if (value) {
          this.form.amount = value.quoteAmount
          this.investedAmount = value.quoteAmount
          this.investShow = true
        } else {
          this.form.amount = ''
          this.form.convertedAmount = 0
          this.investShow = false
        }
      }
    }
  }
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .investing {
    margin-top: 18px;

    /*.investing-block {
      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }
    .invest__input-field {
      input {
        color: #8BB8C1;
        -webkit-text-fill-color: #8BB8C1 !important;
      }
      &:after {
        background-color: #8BB8C1;
      }
    }
    .invest__select-quote-assets {
      input {
        -webkit-text-fill-color: #000000 !important;
        color: #000000;
        font-weight: bold;
      }
      &:after {
        background-color: #8BB8C1;
      }
    }
    .w100 {
      width: 100%;
    }
    .w70 {
      width: 65%;
      padding:  0.3125em 1em 0.3125em 0;
    }
    .w30 {
      width: 35%;
      padding:  0.3125em 0 0.3125em 1em;
    }
    .invest__submit-btn {
      display: flex;
      justify-content: center;
      align-content: center;
      background: #FF873E;
      box-sizing: border-box;
      border-radius: 10px;
      color: #fff !important;
      height: 60px;
      width: 100%;
      font-size: 20px;
      line-height: 120%;
      padding-left: 35px;
      padding-right: 35px;
    }*/


    .investing-block {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: nowrap;

      & .invest__input-field {
        flex: 2 2 auto;

      }

      & .invest__select-field {
        flex: 0 3 auto;
        margin: 0 12px 0 -18px;
        background-color: $col-background-calculated;
      }

      & .btn:last-child {
        margin-left: 12px;
      }
    }

    .invest__edit-btn,
    .invest__cancel-btn {
      width: 100%;
      height: auto;
      padding: 12px 20px;
      margin-top: 8px;
      color: $col-text;

      @include border(1px, 6px);

      .md-button-content {
        display: flex;
        justify-content: space-between;
        align-content: center;
        width: 100%;
        text-transform: none !important;
        * {
          display: block;
        }
      }
    }
    .invest__edit-btn {
      i {
        color: rgba(0, 194, 176, 0.5) !important;
      }
    }
    .invest__cancel-btn {
      i {
        color: rgba(255, 135, 62, 0.5) !important;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/vue/app/sales/sale_details/components/Sales.Invest.vue